import { useEffect, useRef, useState } from 'react';

export type ImageKebabMenuProps = {
  currentAlignment: 'left' | 'center' | 'right';
  onReplace: () => void;
  onAlignLeft: () => void;
  onAlignCenter: () => void;
  onAlignRight: () => void;
  onEditAlt: () => void;
  onDelete: () => void;
  onOpenChange?: (open: boolean) => void;
};

export function ImageKebabMenu(props: ImageKebabMenuProps) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    props.onOpenChange?.(open);
  }, [open, props.onOpenChange]);

  useEffect(() => {
    function onDocClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node))
        setOpen(false);
    }
    if (open) document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [open]);

  function handleAndClose(handler: () => void) {
    return () => {
      handler();
      setOpen(false);
    };
  }

  return (
    <div className="image-kebab" ref={ref}>
      <button
        type="button"
        className="image-kebab__trigger"
        aria-label="Image options"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
      >
        ⋮
      </button>
      {open && (
        <div className="image-kebab__dropdown" role="menu">
          <button
            type="button"
            role="menuitem"
            onClick={handleAndClose(props.onReplace)}
          >
            ↻ Replace image
          </button>
          <button
            type="button"
            role="menuitem"
            aria-pressed={props.currentAlignment === 'left'}
            onClick={handleAndClose(props.onAlignLeft)}
          >
            ⬅ Align left
          </button>
          <button
            type="button"
            role="menuitem"
            aria-pressed={props.currentAlignment === 'center'}
            onClick={handleAndClose(props.onAlignCenter)}
          >
            ⬛ Align center
          </button>
          <button
            type="button"
            role="menuitem"
            aria-pressed={props.currentAlignment === 'right'}
            onClick={handleAndClose(props.onAlignRight)}
          >
            ➡ Align right
          </button>
          <button
            type="button"
            role="menuitem"
            onClick={handleAndClose(props.onEditAlt)}
          >
            ✎ Edit alt text
          </button>
          <hr />
          <button
            type="button"
            role="menuitem"
            className="image-kebab__delete"
            onClick={handleAndClose(props.onDelete)}
          >
            🗑 Delete
          </button>
        </div>
      )}
    </div>
  );
}
